import { Drawer, Stepper } from "@caprikorn/dynamo-ui";
import { type FC, useId, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useReferences } from "@contexts/References/References.context";
import { FORM_VALIDATION_MODE, LS_KEYS } from "@core/core.constants";
import { ArrowLeftMd, ArrowRightMd } from "@icons";
import type {
  GetGroup,
  HelpTechRequestData,
} from "@services/apiEcms/apiEcms.types";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { usePostTechnicalRequest } from "@services/apiEcms/usePostTechnicalRequest";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { ActionTypeForm } from "./ActionTypeForm/ActionTypeForm";
import { CancellationForm } from "./CancellationForm/CancellationForm";
import { ContactDetailsForm } from "./ContactDetails/ContactDetailsForm";
import { PerimeterForm } from "./PerimeterForm/PerimeterForm";
import { PotencyForm } from "./PotencyForm/PotencyForm";
import { RemarkForm } from "./RemarkForm/RemarkForm";
import classes from "./RequestDrawer.module.css";
import type {
  RequestDrawerProps,
  TechRequestDataForm,
} from "./RequestDrawer.types";

const getGroupName = (groupDetail: GetGroup) => groupDetail.group.name;

export const RequestDrawer: FC<RequestDrawerProps> = ({ onHide }) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const { currentGroup } = useGroupReferences();
  const { references } = useReferences();
  const { customer, contract, site } = references;
  const [step, setStep] = useState(0);
  const incrementStep = () => setStep((prev) => prev + 1);

  const { data: groupName } = useGetGroup({
    groupId: currentGroup,
    options: {
      select: getGroupName,
    },
  });

  const { control, handleSubmit, trigger } = useForm<TechRequestDataForm>({
    values: {
      perimeterForm: {
        perimeter: groupName ?? "",
        customer: customer?.id ? `${customer?.name} (${customer?.id})` : "",
        contract: contract?.id ? `${contract?.name} (${contract?.id})` : "",
        site: site?.id ? `${site?.name} (${site?.id})` : "",
      },
      contactForm: {
        email: localStorage.getItem(LS_KEYS.EMAIL) ?? undefined,
      },
    },
    mode: FORM_VALIDATION_MODE,
  });

  const actionType = useWatch({ control, name: "actionTypeForm.actionType" });
  const onlyOneContactPotency = useWatch({
    control,
    name: "potencyForm.onlyOneContact",
  });
  const onlyOneContactCancellation = useWatch({
    control,
    name: "cancellationForm.onlyOneContact",
  });
  const hasDifferentOnSiteContact =
    (actionType === "POTENCY_COST" && !onlyOneContactPotency) ||
    (actionType === "CANCELLATION" && !onlyOneContactCancellation);

  const { mutate, isPending } = usePostTechnicalRequest();

  const onRequestSubmit = (data: TechRequestDataForm) => {
    // We need the last defined value among perimeter, customer and contract (in this order)
    const identifier = [
      data.perimeterForm.perimeter,
      data.perimeterForm.customer,
      data.perimeterForm.contract,
    ]
      .filter(Boolean)
      .reverse()[0];
    const payload: HelpTechRequestData = {
      description: {
        perimeter: {
          group: data.perimeterForm.perimeter,
          customer: data.perimeterForm.customer || "N/A",
          contract: data.perimeterForm.contract || "N/A",
          site: data.perimeterForm.site || "N/A",
        },
        contact: {
          civility: data.contactForm?.civility || "Mr",
          lastName: data.contactForm?.lastName || "",
          firstName: data.contactForm?.firstName || "",
          phone: data.contactForm?.phone || "",
          email: data.contactForm?.email || "",
        },
        comment: {
          comment: data.remarkForm?.comment || "",
        },
      },
      identifier: identifier || "",
      actionType: data.actionTypeForm?.actionType || "OTHERS",
      attachments: data.remarkForm?.attachments,
      siteId: data.perimeterForm.site,
    };

    if (data.actionTypeForm?.actionType === "POTENCY_COST") {
      payload.dueDate = data.potencyForm?.dueDate
        ? new Date(data.potencyForm?.dueDate).toDateString()
        : undefined;
      payload.description.potencyForm = {
        potency: data.potencyForm?.potency || "",
        options: data.potencyForm?.tariffOption || "",
        date: t("date", {
          ns: "common",
          date:
            data.potencyForm?.dueDate && new Date(data.potencyForm?.dueDate),
          formatParams: {
            date: {
              dateStyle: "short",
            },
          },
        }),
      };
    }

    if (data.actionTypeForm?.actionType === "CANCELLATION") {
      payload.dueDate = data.cancellationForm?.dueDate
        ? new Date(data.cancellationForm?.dueDate).toDateString()
        : undefined;
      payload.description.cancellationForm = {
        date: t("date", {
          ns: "common",
          date:
            data.cancellationForm?.dueDate &&
            new Date(data.cancellationForm?.dueDate),
          formatParams: {
            date: {
              dateStyle: "short",
            },
          },
        }),
      };
    }

    if (hasDifferentOnSiteContact) {
      payload.description.onSiteContact = {
        civility: data.siteContactForm?.civility || "Mr",
        lastName: data.siteContactForm?.lastName || "",
        firstName: data.siteContactForm?.firstName || "",
        phone: data.siteContactForm?.phone || "",
        email: data.siteContactForm?.email || "",
      };
    }

    mutate(payload, {
      onSuccess: () => {
        onHide();
      },
    });
  };

  const formId = useId();

  const onNextClick = async () => {
    let isValid;

    if (step === 0) {
      isValid = true; // No validation at this step is needed because all inputs are disabled
    }
    if (step === 1) {
      isValid = await trigger(["actionTypeForm"]);
    }
    if (step === 2) {
      isValid = await trigger(["contactForm"]);
    }
    if (step === 3 && actionType === "CANCELLATION") {
      isValid = await trigger(["cancellationForm"]);
    }
    if (step === 3 && actionType === "POTENCY_COST") {
      isValid = await trigger(["potencyForm"]);
    }
    if (step === 4 && hasDifferentOnSiteContact) {
      isValid = await trigger(["siteContactForm"]);
    }

    if (isValid) incrementStep();
  };

  const isAdvancedRequest = ["CANCELLATION", "POTENCY_COST"].some(
    (element) => element === actionType,
  );
  const additionalStepsNumber = [
    hasDifferentOnSiteContact,
    isAdvancedRequest,
  ].filter(Boolean).length;
  const finalStep = 3 + additionalStepsNumber;

  return (
    <Drawer onClose={onHide} size="lg">
      <Drawer.Header title={t("requestDrawer.title")} />
      <Drawer.Body>
        <form
          className={classes.form}
          id={formId}
          onSubmit={handleSubmit(onRequestSubmit)}
        >
          <Stepper currentStepIndex={step}>
            <Stepper.Step
              label={t("requestDrawer.steps.perimeter.title")}
              onClick={() => setStep(0)}
              stepIndex={0}
            />
            <Stepper.Step
              label={t("requestDrawer.steps.actionType.title")}
              onClick={() => setStep(1)}
              stepIndex={1}
            />
            <Stepper.Step
              label={t("requestDrawer.steps.requesterInfo.title")}
              onClick={() => setStep(2)}
              stepIndex={2}
            />
            {actionType === "CANCELLATION" && (
              <Stepper.Step
                label={t("requestDrawer.steps.cancellation.title")}
                onClick={() => setStep(3)}
                stepIndex={3}
              />
            )}
            {actionType === "POTENCY_COST" && (
              <Stepper.Step
                label={t("requestDrawer.steps.potency.title")}
                onClick={() => setStep(3)}
                stepIndex={3}
              />
            )}
            {hasDifferentOnSiteContact && (
              <Stepper.Step
                label={t("requestDrawer.steps.representativeInfo.title")}
                onClick={() => setStep(4)}
                stepIndex={4}
              />
            )}
            <Stepper.Step
              label={t("requestDrawer.steps.remark.title")}
              stepIndex={finalStep} // This step's index depends on the action type and whether there is a specific on-site contact
            />
          </Stepper>

          <div className={classes.steps}>
            {step === 0 && <PerimeterForm control={control} />}
            {step === 1 && (
              <ActionTypeForm control={control} trigger={trigger} />
            )}
            {step === 2 && (
              <ContactDetailsForm control={control} name="contactForm" />
            )}
            {step === 3 && actionType === "CANCELLATION" && (
              <CancellationForm control={control} />
            )}
            {step === 3 && actionType === "POTENCY_COST" && (
              <PotencyForm control={control} />
            )}
            {step === 4 && hasDifferentOnSiteContact && (
              <ContactDetailsForm control={control} name="siteContactForm" />
            )}
            {step === finalStep && (
              <RemarkForm actionType={actionType} control={control} />
            )}
          </div>
        </form>
      </Drawer.Body>
      <Drawer.Footer>
        {step !== 0 && (
          <Drawer.Footer.CancelButton
            disabled={isPending}
            onClick={() => setStep((prev) => prev - 1)}
            startIcon={ArrowLeftMd}
          >
            {t("button.previous", { ns: "common" })}
          </Drawer.Footer.CancelButton>
        )}
        {step === finalStep ? (
          <Drawer.Footer.ConfirmButton
            key="submit" // DO NOT REMOVE
            endIcon={ArrowRightMd}
            form={formId}
            isLoading={isPending}
            type="submit"
          >
            {t("requestDrawer.submit")}
          </Drawer.Footer.ConfirmButton>
        ) : (
          <Drawer.Footer.ConfirmButton
            key="next" // DO NOT REMOVE
            endIcon={ArrowRightMd}
            form={formId}
            onClick={onNextClick}
          >
            {t("button.next", { ns: "common" })}
          </Drawer.Footer.ConfirmButton>
        )}
      </Drawer.Footer>
    </Drawer>
  );
};
